// src/pages/ExperienceRecords.jsx

import React, { useState, useEffect } from 'react';
import { db } from "../firebase";
import { collection, getDocs, query, orderBy, doc, updateDoc } from "firebase/firestore";
import ExperienceLetter from "./ExperienceLetter";
import EditExperienceRecordModal from "../components/EditExperienceRecordModal"; // Import the new modal
import { FaArchive, FaSearch, FaEdit } from "react-icons/fa"; // Import FaEdit
import "./ExperienceRecords.css";

const ExperienceRecords = () => {
    const [allRecords, setAllRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewingRecord, setViewingRecord] = useState(null);
    const [editingRecord, setEditingRecord] = useState(null); // State for the modal

    const fetchAllRecords = async () => {
        setLoading(true);
        try {
            const recordsRef = collection(db, "experience_records");
            const q = query(recordsRef, orderBy("createdAt", "desc"));
            const snap = await getDocs(q);
            const allRecordsFromDB = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // ✨ Filter to show only resigned employees (those with leaving date)
            const resignedRecords = allRecordsFromDB.filter(record => record.leavingDate);
            
            setAllRecords(resignedRecords);
            setFilteredRecords(resignedRecords);
        } catch (error) {
            console.error("Failed to fetch experience records:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchAllRecords();
    }, []);

    useEffect(() => {
        const results = allRecords.filter(record =>
            record.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredRecords(results);
    }, [searchTerm, allRecords]);

    const handleSaveRecord = async (recordId, updatedData) => {
        try {
            const docRef = doc(db, "experience_records", recordId);
            await updateDoc(docRef, updatedData);
            // Refresh data locally for instant UI update
            fetchAllRecords();
        } catch (error) {
            console.error("Failed to save record:", error);
            alert("Error saving record.");
        }
    };

    if (loading) {
        return <div className="loading-state">Loading all experience records...</div>;
    }

    if (viewingRecord) {
        return <ExperienceLetter record={viewingRecord} onBack={() => setViewingRecord(null)} />;
    }

    return (
        <div className="experience-records-container">
            <h3><FaArchive /> Resigned Employees - Experience Records</h3>
            <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="Search by name or employee ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="table-wrapper">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Employee Name</th>
                            <th>ID</th>
                            <th>Position</th>
                            <th>Joining Date</th>
                            <th>Leaving Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRecords.length > 0 ? filteredRecords.map(record => (
                            <tr key={record.id}>
                                <td data-label="Name">{record.employeeName}</td>
                                <td data-label="ID">{record.employeeId}</td>
                                <td data-label="Position">{record.position}</td>
                                <td data-label="Joining Date">{record.joiningDate}</td>
                                <td data-label="Leaving Date">{record.leavingDate || 'Present'}</td>
                                <td data-label="Actions">
                                    <div className="action-buttons">
                                        <button 
                                            onClick={() => setEditingRecord(record)}
                                            className="edit-btn"
                                            data-tooltip="Edit Record"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button 
                                            onClick={() => setViewingRecord(record)}
                                            className="action-btn-primary"
                                            disabled={!record.leavingDate}
                                            data-tooltip={!record.leavingDate ? "Letter available after leaving date is set" : "View Experience Letter"}
                                        >
                                            View Letter
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6">No records found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Render the modal when a record is being edited */}
            {editingRecord && (
                <EditExperienceRecordModal 
                    record={editingRecord}
                    onSave={handleSaveRecord}
                    onClose={() => setEditingRecord(null)}
                />
            )}
        </div>
    );
};

export default ExperienceRecords;